import { useMemo } from 'react';
import { Minimize2, X } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ModalIconButton } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { RunLog } from '../components/agentStatus/RunLog';
import { RUN_PARAM, agentTabPath } from '../components/agentStatus/runLogPath';
import styles from '../components/agentStatus/AgentStatus.module.css';
import { usePracticeAgents } from '../hooks/useCommandCenter';

/**
 * One agent's run log at one location, on its own linkable page (`?run=<id>` opens that run).
 * Collapse goes back to the side panel over the agent tab; close goes back to the tab.
 */
export function AgentRunLogPage() {
  const { practiceId = '', locationId = '', roleKey = '' } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const agents = usePracticeAgents(practiceId);
  const row = useMemo(
    () =>
      agents.data?.find((agent) => agent.locationId === locationId && agent.roleKey === roleKey),
    [agents.data, locationId, roleKey],
  );
  const agentName = row?.agentName ?? roleKey;
  const focusRunId = searchParams.get(RUN_PARAM) ?? undefined;

  return (
    <div className={styles.page}>
      <PageHeader
        title={`${agentName}'s run log`}
        subtitle={row ? row.locationName : undefined}
        actions={
          <div className={styles.pageActions}>
            <ModalIconButton
              label="Collapse to side panel"
              onClick={() =>
                navigate(agentTabPath(practiceId, locationId, { roleKey, runId: focusRunId }))
              }
            >
              <Minimize2 size={16} aria-hidden />
            </ModalIconButton>
            <ModalIconButton
              label="Close"
              onClick={() => navigate(agentTabPath(practiceId, locationId))}
            >
              <X size={16} aria-hidden />
            </ModalIconButton>
          </div>
        }
      />
      <RunLog
        practiceId={practiceId}
        locationId={locationId}
        roleKey={roleKey}
        focusRunId={focusRunId}
        agentName={agentName}
      />
    </div>
  );
}
